import { HomePage } from '@/components/home-page'
import { QueryErrorScreen } from '@/components/query-error-screen'
import { ErrorAlert } from '@/components/ui/error-alert'
import { PageLoader } from '@/components/ui/page-loader'
import {
  MissingRequiredDataError,
  useAccountQuery,
  useAccountRoomsQuery,
  useInitializeQuote,
  usePhasesQuery,
  usePrimaryContactQuery,
  useQuoteDetailsQuery,
  useQuoteQuery,
  useSystemsQuery,
} from '@/lib/api'
import { useCurrentRoomReconciliation } from '@/lib/hooks/use-current-room-reconciliation'
import { ensureSignedIn } from '@/lib/store'
import { deriveErrorMessage } from '@/lib/utils'
import { createFileRoute } from '@tanstack/react-router'
import { memo, useEffect, useState } from 'react'
import { z } from 'zod'

const HomeRoute = memo(function HomeRoute() {
  const { isPending: isLoadingQuote, error: quoteError, refetch: refetchQuote } = useQuoteQuery()
  const { isPending: isLoadingAccount, error: accountError, refetch: refetchAccount } = useAccountQuery()
  const { isLoading: isLoadingContact, error: contactError, refetch: refetchContact } = usePrimaryContactQuery()
  const { isPending: isLoadingSystems, error: systemsError, refetch: refetchSystems } = useSystemsQuery()
  const { isPending: isLoadingPhases, error: phasesError, refetch: refetchPhases } = usePhasesQuery()
  const { isPending: isLoadingRooms, error: roomsError, refetch: refetchRooms } = useAccountRoomsQuery()
  const {
    isPending: isLoadingQuoteDetails,
    error: quoteDetailsError,
    refetch: refetchQuoteDetails,
  } = useQuoteDetailsQuery()
  const { initialized, error: initializedError } = useInitializeQuote()

  // A room deleted in another tab/session would otherwise leave this one
  // filtered to a ghost. CPQ-only — the unauth proposal route has no room filter.
  useCurrentRoomReconciliation()

  if (quoteError) {
    return <QueryErrorScreen key="quote" title="Error loading quote" error={quoteError} onRetry={refetchQuote} />
  }

  if (accountError) {
    return <QueryErrorScreen key="account" title="Error loading account" error={accountError} onRetry={refetchAccount} />
  }

  // Only repairable contact data blocks the page — plain fetch failures degrade
  // to a blank contact, as they did before the fix-missing-data flow existed.
  if (contactError instanceof MissingRequiredDataError) {
    return <QueryErrorScreen key="contact" title="Error loading contact" error={contactError} onRetry={refetchContact} />
  }

  if (systemsError) {
    return <QueryErrorScreen key="systems" title="Error loading systems" error={systemsError} onRetry={refetchSystems} />
  }

  if (phasesError) {
    return <QueryErrorScreen key="phases" title="Error loading phases" error={phasesError} onRetry={refetchPhases} />
  }

  if (roomsError) {
    return <QueryErrorScreen key="rooms" title="Error loading rooms" error={roomsError} onRetry={refetchRooms} />
  }

  if (quoteDetailsError) {
    return (
      <QueryErrorScreen key="quote-details" title="Error loading quote details" error={quoteDetailsError} onRetry={refetchQuoteDetails} />
    )
  }

  if (isLoadingQuote) {
    return <PageLoader message="Loading quote..." />
  }

  if (isLoadingAccount) {
    return <PageLoader message="Loading account..." />
  }

  if (isLoadingContact) {
    return <PageLoader message="Loading contact..." />
  }

  if (isLoadingSystems) {
    return <PageLoader message="Loading systems..." />
  }

  if (isLoadingPhases) {
    return <PageLoader message="Loading phases..." />
  }

  if (isLoadingRooms) {
    return <PageLoader message="Loading rooms..." />
  }

  if (isLoadingQuoteDetails) {
    return <PageLoader message="Loading items..." />
  }

  if (initializedError) {
    return <ErrorAlert title="Error initializing quote" message={deriveErrorMessage(initializedError)} />
  }

  if (!initialized) {
    return <PageLoader message="Initializing quote..." />
  }

  return <HomePage />
})

type SignInState = { status: 'checking' } | { status: 'ready' } | { status: 'failed'; error: unknown }

/**
 * The builder renders once a token is in hand, not merely once an account is
 * cached: an account whose session can no longer renew would otherwise open
 * the quote and fail every read. Nothing is unsaved yet, so sign in right away.
 */
const AuthenticatedHomeRoute = memo(function AuthenticatedHomeRoute() {
  const [signIn, setSignIn] = useState<SignInState>({ status: 'checking' })
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    ensureSignedIn()
      .then((result) => {
        if (!cancelled && result === 'ready') setSignIn({ status: 'ready' })
      })
      .catch((error) => {
        if (!cancelled) setSignIn({ status: 'failed', error })
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  if (signIn.status === 'failed') {
    return (
      <ErrorAlert
        title="Error signing in"
        message={deriveErrorMessage(signIn.error)}
        onRetry={() => {
          setSignIn({ status: 'checking' })
          setAttempt((n) => n + 1)
        }}
      />
    )
  }

  if (signIn.status === 'checking') {
    return <PageLoader message="Signing in..." />
  }

  return <HomeRoute />
})

export const Route = createFileRoute('/')({
  validateSearch: z.object({
    id: z.string(),
    action: z.enum(['activate', 'close-lost']).optional(),
  }),
  component: AuthenticatedHomeRoute,
})
